import { httpClient } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type {
  Recipe,
  RecipeCreateInput,
  RecipeImportResult,
  RecipeListParams,
  RecipeUpdateInput,
} from '@/domain/entities/recipe.entity'
import type { RecipeRepository } from '@/domain/repositories/recipe.repository'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { RecipeDto } from './recipe.dto'
import { recipeMapper } from './recipe.mapper'

export class RecipeHttpRepository implements RecipeRepository {
  async list(outletId: string, params: RecipeListParams): Promise<Paged<Recipe>> {
    const raw = await httpClient.get<unknown>('/recipes', {
      params: {
        outlet_id: outletId,
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
        active: params.active !== null ? params.active : undefined,
      },
    })
    const paged = unwrapPaged<RecipeDto>(raw, params.page, params.limit)
    return { ...paged, items: paged.items.map(recipeMapper.toRecipe) }
  }

  async getById(id: string): Promise<Recipe> {
    const raw = await httpClient.get<unknown>(`/recipes/${id}`)
    return recipeMapper.toRecipe(unwrap<RecipeDto>(raw))
  }

  async create(outletId: string, input: RecipeCreateInput): Promise<Recipe> {
    const body = {
      outlet_id: outletId,
      ...recipeMapper.toCreateBody(input),
    }
    const raw = await httpClient.post<unknown>('/recipes', body)
    return recipeMapper.toRecipe(unwrap<RecipeDto>(raw))
  }

  async update(id: string, input: RecipeUpdateInput): Promise<Recipe> {
    const body = recipeMapper.toUpdateBody(input)
    const raw = await httpClient.put<unknown>(`/recipes/${id}`, body)
    return recipeMapper.toRecipe(unwrap<RecipeDto>(raw))
  }

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/recipes/${id}`)
  }

  async restore(id: string): Promise<Recipe> {
    const recipe = await this.getById(id)
    const body = recipeMapper.toUpdateBody({ ...recipe, active: true })
    const raw = await httpClient.put<unknown>(`/recipes/${id}`, body)
    return recipeMapper.toRecipe(unwrap<RecipeDto>(raw))
  }

  async importXlsx(outletId: string, file: File): Promise<RecipeImportResult> {
    const form = new FormData()
    form.append('outlet_id', outletId)
    form.append('file', file)
    const raw = await httpClient.post<unknown>('/recipes/import', form)
    const data = unwrap<{
      recipes?: { created?: number; updated?: number }
      failed?: Array<{ sheet: string; row: number; key: string; error: string }>
    }>(raw)
    return {
      recipes: {
        created: data.recipes?.created ?? 0,
        updated: data.recipes?.updated ?? 0,
      },
      failed: (data.failed ?? []).map((f) => ({
        sheet: f.sheet,
        row: f.row,
        key: f.key,
        error: f.error,
      })),
    }
  }

  async exportXlsx(outletId: string): Promise<{ blob: Blob; filename?: string }> {
    return httpClient.download('/recipes/export', { params: { outlet_id: outletId } })
  }
}
